"use client"

import { Box, Typography } from "@mui/material"
import { CarModelSafetySystemItemDto } from "api"
import { useIsDesktop } from "common/MediaQueries"
import { useRef, useState } from "react"
import styled from "styled-components"
import SwiperCore, {
  A11y,
  Controller,
  EffectFade,
  Keyboard,
  Navigation,
} from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import { StyledSpacer } from "theme/shared"
import SafetyVideoCard from "./SafetyVideoCard"
import SwiperControllerRow from "./SwiperControllerRow"

const StyledWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`
const StyledMenuSwiper = styled(Swiper)`
  position: absolute;
  top: 0;
  width: 100%;
  height: 60px;
  background-color: ${(p) => p.theme.colors.transparent};
  max-height: 200px;
  box-sizing: border-box;
  padding: 10px 0;
  .swiper-slide {
    width: auto !important;
    cursor: pointer !important;
    display: flex !important;
    justify-content: center !important;
  }
  .swiper-wrapper {
    display: flex !important;
    justify-content: center !important;
    align-items: center;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    .swiper-wrapper {
      -webkit-transform: none !important;
      transform: none !important;
    }
  }
`
const StyledSwiper = styled(Swiper)`
  width: 100%;
  height: 100%;
  /* todo change this */
  height: calc(100vh - 100px);
  .swiper-slide {
    overflow: hidden;
    margin-right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 80px;
    /* todo change this */

    height: calc(100vh - 100px);
    width: 100vw !important;
    max-width: 100vw !important;
  }
`

const StyledText = styled(Typography).attrs({ variant: "body1" })<{
  $isActive: boolean
}>`
  font-size: 12px;
  color: ${(p) =>
    p.$isActive ? p.theme.colors.black : p.theme.colors.darkGrey};
  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 16px;
  }
`
const StyledBox = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledSwiperControllerRow = styled(SwiperControllerRow)`
  position: absolute;
  bottom: 0;
  z-index: 100;
  justify-content: center;
  width: 100%;
  align-items: flex-start;
`

type CategoriesSwiperProps = {
  data: CarModelSafetySystemItemDto[]
}

const CategoriesSwiper = ({ data }: CategoriesSwiperProps) => {
  const [menuSwiper, setMenuSwiper] = useState<SwiperCore>()
  const [gallerySwiper, setGallerySwiper] = useState<SwiperCore>()
  const navigationPrevRef = useRef<HTMLDivElement>(
    null
  ) as React.MutableRefObject<HTMLDivElement>
  const navigationNextRef = useRef<HTMLDivElement>(
    null
  ) as React.MutableRefObject<HTMLDivElement>
  const [activeIndex, setActiveIndex] = useState(0)
  const isDesktop = useIsDesktop()

  return (
    <StyledWrapper>
      <StyledSwiper
        modules={[Navigation, Controller, EffectFade, A11y, Keyboard]}
        onSwiper={setGallerySwiper}
        controller={{ control: menuSwiper }}
        spaceBetween={10}
        centeredSlides
        slidesPerView={1}
        effect={"fade"}
        keyboard={{
          enabled: true,
        }}
        a11y={{
          slideRole: "tabpanel",
        }}
        navigation={{
          prevEl: navigationPrevRef.current,
          nextEl: navigationNextRef.current,
        }}
      >
        {data.slice(0, 5).map(
          (item, index) =>
            item?.video?.imageUrl && (
              <SwiperSlide
                key={`categoriesSwiper-contect-${index}`}
                id={`${index}-panel`}
                aria-labelledby={`${index}-tab`}
              >
                {({ isActive, isNext, isPrev }) => (
                  <SafetyVideoCard
                    item={item}
                    isNext={isNext || isPrev}
                    isActive={isActive}
                  />
                )}
              </SwiperSlide>
            )
        )}
      </StyledSwiper>
      <StyledMenuSwiper
        modules={[Navigation, Controller, A11y]}
        onSwiper={setMenuSwiper}
        controller={{ control: gallerySwiper }}
        spaceBetween={isDesktop ? 35 : 15}
        slidesPerView="auto"
        touchRatio={0.4}
        slideToClickedSlide={true}
        a11y={{
          slideRole: "tab",
        }}
        onBeforeInit={(swiper) => {
          swiper.wrapperEl.setAttribute("role", "tablist")
          swiper.wrapperEl.setAttribute("aria-orientation", "horizontal")
        }}
        onSlideChange={(swiper) => {
          swiper.slides.map((slide, index) => {
            if (index === swiper.activeIndex) {
              slide.setAttribute("aria-selected", "true")
            } else {
              slide.setAttribute("aria-selected", "false")
            }
          })
        }}
      >
        {data.slice(0, 5).map(
          (item, index) =>
            item?.video?.imageUrl && (
              <SwiperSlide
                key={`categoriesSwiper-tabs-${index}`}
                id={`${index}-tab`}
                aria-controls={`${index}-panel`}
                aria-selected={menuSwiper?.activeIndex == index ? true : false}
                onClick={() => {
                  setActiveIndex(index)
                  gallerySwiper?.slideTo(index)
                  menuSwiper?.slideTo(index)
                }}
              >
                <StyledBox>
                  <StyledText $isActive={index === activeIndex}>
                    {item.shortName}
                  </StyledText>
                </StyledBox>
              </SwiperSlide>
            )
        )}
      </StyledMenuSwiper>
      <StyledSwiperControllerRow
        prevRef={navigationPrevRef}
        nextRef={navigationNextRef}
        setActiveIndex={setActiveIndex}
      >
        <StyledSpacer $w={240} $h={150} />
      </StyledSwiperControllerRow>
    </StyledWrapper>
  )
}

export default CategoriesSwiper
